/**
 * The room, as faces (SPEC §2.3 phase 2 — the prediction is two picks from the
 * full roster, the turn player included, because they rank themselves).
 *
 * DESIGN.md §3.4 drew this as a 2-column grid of avatar-and-name rows. That is
 * reversed: the tiles are the `card` shape now, face above name. The physical
 * game is played with a deck of people, and a predictor hunting for the one
 * person they think goes first is scanning faces, not reading a column of
 * names. A row list also spent half the phone's width on whitespace at the very
 * moment the screen has nothing else to spend it on.
 *
 * ## The grid never scrolls
 *
 * A predictor has one job here and it must be visible in one glance — a grid
 * that scrolls hides two or three people, and the person you cannot see is the
 * person you do not pick. So the grid takes its height from the screen (it is
 * the flex child that grows) and divides it into fixed rows, and the faces take
 * whatever size that leaves.
 *
 * Because the rows are `1fr` of a fixed height, the row *count* is free: four
 * rows of twelve players is not a taller grid, it is four shorter rows. That is
 * what lets the column count be chosen for how wide a card should be rather than
 * to keep the row count down — see `predictionColumns`.
 *
 * ## Why the taken face stays put
 *
 * The player already holding the other end is rendered `disabled` and stays in
 * place. Removing the tile instead would reflow every tile after it — under the
 * finger, between two taps. This rule is carried over verbatim from the picker
 * this grid replaces: nobody can be both ends of one ranking.
 */
import type { PlayerId } from '@idgame/protocol';
import { LicenseCard, type LicensePlayer } from '../LicenseCard.js';
import type { PredictionSlot } from './PredictionSlots.js';

export interface PredictionFaceGridProps {
  /** The full roster, turn player included. Order is the caller's. */
  roster: readonly LicensePlayer[];
  /** The player currently in the FIRST slot, if any. */
  first: PlayerId | null;
  /** The player currently in the LAST slot, if any. */
  last: PlayerId | null;
  /**
   * The slot a tap fills. `null` means no slot is waiting — both answers are in
   * and the screen is on Submit — so the grid is inert until a slot is re-armed.
   */
  armed: PredictionSlot | null;
  onPick: (id: PlayerId) => void;
  /** The hard lock has fired, or this player has locked in. */
  disabled?: boolean;
  className?: string;
}

/**
 * Two columns to 6 players, three above that. Never four.
 *
 * The rule used to widen to four columns at 10–12, chosen to keep the grid to at
 * most three rows. That was optimising the wrong axis. Rows here are `1fr` of the
 * height the grid is handed, so a fourth row does not make the grid taller — it
 * makes each row shorter. Four columns therefore bought nothing and cost width:
 * at twelve players a cell came out around 81px wide and 187px tall, a 0.44
 * aspect, and a face that shape reads as a strip rather than as a card.
 *
 * Three columns at the same count is roughly 111×140 — wider, slightly larger in
 * area, and close to the 4:5 of an actual license. Two columns below seven keeps
 * a small room from spreading three narrow cards across a row it does not need.
 *
 * Exported because the screenshot scenes and the layout tests both need to know
 * what the grid will do at a given player count without re-deriving it.
 */
export function predictionColumns(playerCount: number): number {
  return playerCount > 6 ? 3 : 2;
}

export function PredictionFaceGrid({
  roster,
  first,
  last,
  armed,
  onPick,
  disabled = false,
  className = '',
}: PredictionFaceGridProps) {
  const columns = predictionColumns(roster.length);
  const rows = Math.max(1, Math.ceil(roster.length / columns));

  return (
    <div
      data-testid="prediction-face-grid"
      data-columns={columns}
      role="group"
      aria-label="The room"
      className={`grid min-h-0 flex-1 gap-2 ${className}`}
      style={{
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        // Fixed rows rather than auto: the grid is handed a height and splits
        // it, so the last row cannot fall off the bottom of the phone.
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
      }}
    >
      {roster.map((player) => {
        const isFirst = player.id === first;
        const isLast = player.id === last;
        // Whoever holds the slot that is NOT armed is spoken for. While the
        // FIRST slot is armed the LAST pick is unpickable, and vice versa.
        const taken = armed === 'first' ? isLast : armed === 'last' ? isFirst : false;
        const pickable = !disabled && armed !== null && !taken;
        const badge = isFirst ? '1st' : isLast ? 'Last' : undefined;

        const card = (
          <LicenseCard
            player={player}
            variant="card"
            size="tile"
            tone={isFirst ? 'top' : isLast ? 'bottom' : 'neutral'}
            dimmed={taken}
            badge={badge}
            className="h-full"
          />
        );

        if (!pickable) {
          return (
            <div
              key={player.id}
              data-testid={`face-${player.id}`}
              data-state={taken ? 'taken' : badge ? 'picked' : 'idle'}
              aria-disabled="true"
              className="min-h-0"
            >
              {card}
            </div>
          );
        }

        return (
          // `role="button"` rather than a real <button>: a LicenseCard is flow
          // content and flow content inside a <button> is invalid HTML.
          // Keyboard activation is wired by hand to match.
          <div
            key={player.id}
            role="button"
            tabIndex={0}
            aria-pressed={Boolean(badge)}
            aria-label={
              armed === 'first' ? `${player.name} first` : `${player.name} last`
            }
            data-testid={`face-${player.id}`}
            data-state={badge ? 'picked' : 'idle'}
            onClick={() => onPick(player.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onPick(player.id);
              }
            }}
            className="min-h-0 cursor-pointer rounded-card transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green active:scale-[0.98]"
          >
            {card}
          </div>
        );
      })}
    </div>
  );
}
